import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styles from "./AboutSection.module.css";
import imgBgRed from "../assests/images/img-bg-EB455A.png";
import imgBgPurple from "../assests/images/img-bg-A186CF.png";
import imgBgYellow from "../assests/images/img-bg-F9D74A.png";
import imgBgCyan from "../assests/images/img-bg-4FD8DA.png";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * AboutSection Component - Community Showcase
 *
 * Features a community slider with profile cards that show names on hover.
 * Uses GSAP ScrollTrigger for scroll-based animations.
 *
 * @param {Object} props - Component props
 * @param {string} [props.badge] - Badge text above heading
 * @param {string} [props.heading] - Main heading text
 * @param {string} [props.description] - Description text below heading
 *
 * @example
 * <AboutSection
 *   badge="Meet our community"
 *   heading="Designed for creators. Powered by pros."
 *   description="One smart link for everything—socials, store, music, and beyond. Designed to turn clicks into connections."
 * />
 */

// Community members data
const communityMembers = [
  {
    id: 1,
    name: "Michael Chen",
    image: imgBgRed,
    backgroundColor: "#EB455A", // Red/pink
    shape: "rounded",
    position: "left"
  },
  {
    id: 2,
    name: "Emma Rodriguez",
    image: imgBgPurple,
    backgroundColor: "#A186CF", // Light purple
    shape: "circle",
    position: "center-left"
  },
  {
    id: 3,
    name: "David Kim",
    image: imgBgYellow,
    backgroundColor: "#F9D74A", // Yellow
    shape: "rounded",
    position: "center-right"
  },
  {
    id: 4,
    name: "Sophie Martin",
    image: imgBgCyan,
    backgroundColor: "#4FD8DA", // Turquoise/cyan
    shape: "circle",
    position: "right"
  },
  {
    id: 5,
    name: "Alex Thompson",
    image: imgBgRed,
    backgroundColor: "#EB455A", // Red/pink
    shape: "rounded",
    position: "right-extended"
  },
  {
    id: 6,
    name: "Maria Garcia",
    image: imgBgPurple,
    backgroundColor: "#A186CF", // Light purple
    shape: "circle",
    position: "right-extended-2"
  }
];

const AboutSection = ({
  badge = "Meet our community",
  heading = "Designed for creators. Powered by pros.",
  description = "One smart link for everything—socials, store, music, and beyond. Designed to turn clicks into connections.",
}) => {
  const sectionRef = useRef(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    // Create a GSAP context for proper cleanup
    const ctx = gsap.context(() => {
      // Animate badge
      gsap.fromTo(`.${styles.badge}`, 
        {
          y: 30,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay: 0.2,
          ease: "power3.out",
        }
      );

      // Animate heading
      gsap.fromTo(`.${styles.heading}`, 
        {
          y: 50,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          delay: 0.4,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            toggleActions: "play none none none",
          }
        }
      );

      // Animate description
      gsap.fromTo(`.${styles.description}`, 
        {
          y: 40,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          delay: 0.6,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            toggleActions: "play none none none",
          }
        }
      );

      // Animate community cards
      const cards = sectionRef.current.querySelectorAll(`.${styles.communityCard}`);
      cards.forEach((card, index) => {
        gsap.fromTo(card, 
          {
            y: 60,
            opacity: 0,
            scale: 0.8,
          },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.8,
            delay: 0.8 + (index * 0.1),
            ease: "back.out(1.4)",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 70%",
              toggleActions: "play none none none",
            }
          }
        );
      });

      // Sticky horizontal scroll for cards
      const cardsContainer = sectionRef.current.querySelector(`.${styles.communityCards}`);
      if (cardsContainer) {
        const scrollWidth = cardsContainer.scrollWidth;
        const clientWidth = cardsContainer.clientWidth;
        const scrollDistance = scrollWidth - clientWidth;

        if (scrollDistance > 0) {
          gsap.to(cardsContainer, {
            x: -scrollDistance,
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              pin: true,
              start: "top top",
              end: () => `+=${scrollDistance}`,
              scrub: 1,
              anticipatePin: 1,
            }
          });
        }
      }
    }, sectionRef);

    // Cleanup function
    return () => ctx.revert();
  }, []);

  /**
   * Handle card hover - show name overlay
   */
  const handleCardHover = (cardId) => {
    setHoveredCard(cardId);
  };

  /**
   * Handle card leave - hide name overlay
   */
  const handleCardLeave = () => {
    setHoveredCard(null);
  };

  return (
    <section ref={sectionRef} className={styles.about}>
      <div className={styles.container}>
        {/* Badge */}
        <div className={styles.badge}>{badge}</div>

        {/* Heading */}
        <h2 className={styles.heading}>{heading}</h2>

        {/* Description */}
        <p className={styles.description}>{description}</p>

        {/* Community Slider */}
        <div className={styles.communitySlider}>
          <div className={styles.communityCards}>
            {communityMembers.map((member) => (
              <div
                key={member.id}
                className={`${styles.communityCard} ${styles[member.shape]}`}
                style={{ backgroundColor: member.backgroundColor }}
                onMouseEnter={() => handleCardHover(member.id)}
                onMouseLeave={handleCardLeave}
              >
                <div className={styles.cardImageWrapper}>
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className={styles.cardImage}
                  />
                </div>
                {/* Name overlay on hover */}
                {hoveredCard === member.id && (
                  <div className={styles.nameOverlay}>
                    <span className={styles.memberName}>{member.name}</span>
                    <div className={styles.arrowButton}>
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
